import { useCallback, useMemo } from 'react'
import { Power, PowerOff, Zap } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchTriggers, toggleTrigger, AUTO_STR as S, type TriggerRow } from './automationApi'

/**
 * A26.08 — Триггеры (events that fire workflows). Enable/disable a trigger.
 * Dormant until `/admin/automation/triggers` ships. Placeholder USERS_VIEW.
 */
export function TriggersScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<TriggerRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'event', header: S.event, type: 'text' },
      { key: 'workflows', header: S.workflows, type: 'number' },
      { key: 'enabled', header: S.enabled, type: 'text' },
    ],
    [],
  )

  const toggle = useCallback(
    async (row: TriggerRow) => {
      try {
        await toggleTrigger(row.id, !row.enabled)
        toast(S.toggled, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<TriggerRow>[]>(
    () => [
      {
        id: 'disable',
        label: S.disable,
        icon: <PowerOff size={14} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: toggle,
      },
      {
        id: 'enable',
        label: S.enable,
        icon: <Power size={14} />,
        visible: (r) => !r.enabled,
        onSelect: toggle,
      },
    ],
    [toggle],
  )

  const fetcher = useCallback(fetchTriggers, [])
  return (
    <>
      <PageHeader title={S.triggers} screenId="A26.08" />
      <DataTable<TriggerRow>
        screenId="A26.08"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Zap size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
